import React, { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import { TextInput, TextInputProps } from './TextInput';

export interface LockedTextInputProps {
  value: string;
  onChange?: (value: string) => void;
  onFinish?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const LockedTextInput: React.FC<LockedTextInputProps & TextInputProps> = ({
  value,
  onChange,
  onFinish,
  className = '',
  ...textInputProps
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update editValue when value prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);


  const handleFinish = () => {
    if (isEditing) {
      setIsEditing(false);
      if (onFinish) {
        onFinish(editValue);
      }
    }
  };

  // Handle click outside to finish editing
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleFinish();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing, handleFinish]);

  const handleStartEdit = () => {
    if (!textInputProps.disabled) {
      setIsEditing(true);
      setEditValue(value);
    }
  };


  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFinish();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value); // Reset to original value
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const displayClasses = cx(
    'block bg-white rounded-md border-1 border-dashed border-gray-400 p-2 cursor-pointer',
    'hover:border-gray-500 hover:bg-gray-50 transition-colors duration-200',
    textInputProps.disabled ? 'opacity-50 cursor-not-allowed' : '',
    textInputProps.size === 'sm' ? 'p-1 text-sm' : '',
    textInputProps.size === 'md' ? 'p-2 text-md' : '',
    textInputProps.size === 'lg' ? 'p-3 text-lg' : '',
    className
  );

  if (isEditing) {
    return (
      <div ref={containerRef} className={className}>
        <TextInput
          ref={inputRef}
          value={editValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          {...textInputProps}
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={displayClasses}
      onClick={handleStartEdit}
      onKeyDown={(e) => e.key === 'Enter' && handleStartEdit()}
      tabIndex={textInputProps.disabled ? -1 : 0}
      role="button"
      aria-label="Click to edit"
    >
      {value || textInputProps.placeholder}
    </div>
  );
};

export default LockedTextInput; 